/**
 * kula's picker panel is `position: fixed`, but it subtracts the portal
 * target's rect from the coordinates whenever the target is not `document.body`
 * (`rebaseToContainer` in `PopoverPortal`). That only cancels out when the
 * target is a containing block for fixed children — a transform, filter,
 * perspective, `contain`, or `will-change` makes one. A plain `Flex` or a
 * Chakra `ModalContent` does not, so the panel lands offset by the container's
 * own position, usually against the top-left of the window.
 *
 * So take the container only when it can carry the panel, and fall back to the
 * body, where the coordinates are already right.
 */
const FIXED_CONTAINING_BLOCK_HINTS = [
  'transform',
  'perspective',
  'filter',
  'backdropFilter',
] as const;

const formsContainingBlock = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);

  if (
    FIXED_CONTAINING_BLOCK_HINTS.some(
      (property) => style[property] && style[property] !== 'none'
    )
  ) {
    return true;
  }

  // Either value of `contain` is enough on its own, as is naming one of the
  // containing-block properties in `will-change`.
  if (/paint|layout/.test(style.contain)) return true;
  return /transform|perspective|filter/.test(style.willChange);
};

export const resolvePortalTarget = (
  container?: HTMLElement | null
): HTMLElement | null => {
  if (!container || typeof window === 'undefined') return null;
  return formsContainingBlock(container) ? container : null;
};
